import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Subscription tier configurations
 */
export const SUBSCRIPTION_TIERS = {
  STARTER: {
    name: "Starter",
    quizSlots: 2,
    aiRequestsPerDay: 3,
    sessionsPerQuiz: 1,
    vaultAccess: "view-only",
    emailPrizes: false,
    analytics: "limited",
  },
  PRO: {
    name: "Pro",
    quizSlots: 12,
    aiRequestsPerDay: 25,
    sessionsPerQuiz: 10,
    vaultAccess: "full",
    emailPrizes: true,
    analytics: "full",
  },
};

/**
 * Get user subscription status from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<string>} Subscription status ('free', 'active', 'canceled', 'past_due', 'unpaid')
 */
export const getUserSubscription = async (userId) => {
  if (!userId) return "free";

  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      return userData.subscriptionStatus || "free";
    }

    return "free";
  } catch (error) {
    console.error("Error getting user subscription:", error);
    return "free";
  }
};

/**
 * Check if user has Pro subscription
 * Includes active subscriptions and past_due subscriptions within grace period
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} True if user has active Pro subscription (including grace period)
 */
export const isProUser = async (userId) => {
  if (!userId) return false;

  try {
    const subscriptionStatus = await getUserSubscription(userId);

    // Active subscription grants Pro access
    if (subscriptionStatus === "active") {
      return true;
    }

    // past_due status grants Pro access during grace period (3 days)
    if (subscriptionStatus === "past_due") {
      try {
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          const gracePeriodEndDate = userData?.gracePeriodEndDate;

          if (gracePeriodEndDate) {
            // Check if grace period hasn't expired
            const now = new Date();
            const graceEnd = gracePeriodEndDate.toDate
              ? gracePeriodEndDate.toDate()
              : new Date(gracePeriodEndDate);

            if (graceEnd > now) {
              return true; // Still in grace period, grant Pro access
            }
          }
        }
      } catch (error) {
        console.error("Error checking grace period:", error);
        // Default to no Pro access if we can't verify grace period
      }
    }

    return false;
  } catch (error) {
    console.error("Error checking Pro status:", error);
    return false;
  }
};

/**
 * Get subscription limits for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Subscription tier configuration with limits
 */
export const getSubscriptionLimits = async (userId) => {
  const isPro = await isProUser(userId);
  return isPro ? SUBSCRIPTION_TIERS.PRO : SUBSCRIPTION_TIERS.STARTER;
};

/**
 * Get subscription tier name
 * @param {string} userId - User ID
 * @returns {Promise<string>} Tier name ('Starter' or 'Pro')
 */
export const getSubscriptionTier = async (userId) => {
  const limits = await getSubscriptionLimits(userId);
  return limits.name;
};

/**
 * Check if user can perform a premium action
 * @param {string} userId - User ID
 * @param {string} action - Action to check ('createForm', 'createSession', 'useAI', 'accessVault', 'emailPrizes', 'fullAnalytics')
 * @returns {Promise<boolean>} True if user can perform the action
 */
export const canPerformAction = async (userId, action) => {
  const limits = await getSubscriptionLimits(userId);
  const isPro = limits.name === "Pro";

  switch (action) {
    case "createForm":
      return true; // Both tiers can create forms, but limits differ
    case "createSession":
      return true; // Both tiers can create sessions, but limits differ
    case "useAI":
      return true; // Both tiers can use AI, but limits differ
    case "accessVault":
      return isPro; // Only Pro can add vault quizzes to forms
    case "emailPrizes":
      return isPro; // Only Pro can use email prizes
    case "fullAnalytics":
      return isPro; // Only Pro can access full analytics
    default:
      return false;
  }
};
